import React, { useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import {
  createPayment,
  verifyPayment,
  loadRazorpayScript,
} from '../../services/paymentService';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatCurrency';
import { isAdvancePaymentRequired } from '../../utils/bookingConfig';
import './Payment.css';

const Payment = ({ onSuccess, onBack }) => {
  const { booking, updateBooking } = useBooking();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const vehicle = booking.selectedVehicle || booking.selectedPackage;
  const amount = Number(booking.booking?.advance_amount ?? 0);
  const requiresAdvancePayment = isAdvancePaymentRequired(booking.tripType);

  const handlePay = async () => {
    if (!requiresAdvancePayment) {
      updateBooking('paymentVerification', {
        booking_id: booking.booking?.booking_id || booking.booking?._id,
        status: 'confirmed',
        payment_status: 'not_required',
        payment_type: 'rental_confirmation',
        total_amount: Number(booking.booking?.total_amount ?? 0),
        advance_amount: 0,
        paid_amount: 0,
        remaining_amount: Number(booking.booking?.total_amount ?? 0),
      });
      onSuccess?.();
      return;
    }

    setError('');
    setProcessing(true);
    try {
      const bookingId = booking.booking?.booking_id || booking.booking?._id;
      if (!bookingId) throw new Error('Booking ID missing from response');
      if (!amount || amount <= 0) throw new Error('Booking advance amount is unavailable');

      // Create the Razorpay order for the booking already shown to the user.
      const payRes = await createPayment(bookingId);
      const payData = payRes?.data?.data || payRes?.data;
      if (!payData?.razorpay_order_id)
        throw new Error('Payment order could not be created');
      // if (Number(payData.amount) !== Math.round(amount * 100)) {
      //   throw new Error('Payment order amount does not match the booking advance.');
      // }

      updateBooking('payment', payData);

      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load Razorpay');

      await new Promise((resolve, reject) => {
        const options = {
          key: payData.razorpay_key_id,
          amount: payData.amount,
          currency: payData.currency || 'INR',
          name: 'Jayraj cabs',
          description: `Booking ${bookingId}`,
          order_id: payData.razorpay_order_id,
          theme: { color: '#A67C00' },
          handler: async (response) => {
            try {
              const verificationResponse = await verifyPayment({
                booking_id: bookingId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              if (verificationResponse?.success === false) {
                throw new Error(verificationResponse.message || 'Payment verification failed.');
              }
              updateBooking(
                'paymentVerification',
                verificationResponse?.data || verificationResponse
              );
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      });

      onSuccess?.();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="pay-card">
      <button className="pay-back" onClick={onBack}>← Back</button>
      <h2 className="pay-title">Payment</h2>

      <div className="pay-summary">
        <SummaryRow label="Booking ID" value={booking.booking?.booking_id || booking.booking?._id || '—'} />
        <SummaryRow label="Ride mode" value={booking.tripType === 'rental' ? 'Rental' : 'Outstation'} />
        <SummaryRow label="Pickup" value={booking.pickup?.name || '—'} />
        {booking.dropoff?.name && (
          <SummaryRow label="Drop" value={booking.dropoff.name} />
        )}
        <SummaryRow label="Date" value={formatDate(booking.date)} />
        {booking.dropDate && <SummaryRow label="Drop date" value={formatDate(booking.dropDate)} />}
        {booking.dropTime && <SummaryRow label="Drop time" value={booking.dropTime} />}
        {vehicle?.vehicle_type && (
          <SummaryRow label="Vehicle" value={vehicle.vehicle_type} />
        )}
        {booking.tripType === 'outstation' && (
          <SummaryRow label="Trip type" value={booking.rideMode === 'round_trip' ? 'Round trip' : 'One way'} />
        )}
        {booking.returnDate && <SummaryRow label="Return" value={formatDateTime(booking.returnDate)} />}
        {booking.pickupTime && <SummaryRow label="Pickup time" value={booking.pickupTime} />}
        {requiresAdvancePayment && (
          <div className="pay-total">
            <span>Advance Payable</span>
            <strong>{formatCurrency(amount)}</strong>
          </div>
        )}
      </div>

      {error && <p className="pay-error">{error}</p>}

      <button className="pay-cta" disabled={processing} onClick={handlePay}>
        {processing
          ? 'Confirming...'
          : requiresAdvancePayment
            ? `Pay Now ${formatCurrency(amount)}`
            : 'Confirm Booking'}
      </button>

      <p className="pay-note">
        {requiresAdvancePayment
          ? '🔒 Payments are secured by Razorpay'
          : 'Rental payment is due after your trip is completed.'}
      </p>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="pay-row">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

export default Payment;